import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { AVATAR_COLORS, LIMITS } from "@checklist/shared";
import { Avatar } from "../components/Avatar.js";
import {
  ErrorBanner,
  fieldCls,
  labelCls,
  SuccessBanner,
} from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";
import { useConfirm } from "../components/ui/ConfirmDialog.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { useRegisterUnsaved } from "../components/UnsavedChangesProvider.js";
import { useAuth } from "../context/AuthContext.js";
import {
  ApiError,
  deleteAvatar,
  fetchMe,
  getAvatarUploadUrl,
  updateMyProfile,
  uploadAvatarJpeg,
  type MeProfile,
} from "../lib/api.js";
import { resizeImageToJpeg } from "../lib/avatarImage.js";
import { diffCount } from "../lib/unsavedChanges.js";

export function ProfilePage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [screenName, setScreenName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatarColor, setAvatarColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function syncFromProfile(me: MeProfile) {
    setProfile(me);
    setScreenName(me.screenName);
    setFirstName(me.firstName ?? "");
    setLastName(me.lastName ?? "");
    setAvatarColor(me.avatarColor ?? null);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const me = await fetchMe();
        if (cancelled) return;
        syncFromProfile(me);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Failed to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const savedProfile = {
    screenName: profile?.screenName ?? "",
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    avatarColor: profile?.avatarColor ?? null,
  };
  const unsavedCount = diffCount(savedProfile, {
    screenName,
    firstName,
    lastName,
    avatarColor,
  });
  useRegisterUnsaved("profile", loading ? 0 : unsavedCount);

  async function onSaveProfile(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const me = await updateMyProfile({
        screenName,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        avatarColor,
      });
      syncFromProfile(me);
      setSuccess("Profile saved.");
      window.dispatchEvent(new CustomEvent("profile-updated"));
    } catch (err) {
      if (err instanceof ApiError && err.code === "LIMIT_EXCEEDED") {
        setError(`Screen name must be at most ${LIMITS.screenNameMax} characters.`);
      } else if (err instanceof ApiError && err.code === "RESERVED_SCREEN_NAME") {
        setError("That screen name is reserved. Choose a different one.");
      } else if (err instanceof ApiError && err.code === "INVALID_SCREEN_NAME") {
        setError("Enter a screen name.");
      } else if (err instanceof ApiError && err.code === "INVALID_FIRST_NAME") {
        setError(`First name must be at most ${LIMITS.firstNameMax} characters.`);
      } else if (err instanceof ApiError && err.code === "INVALID_LAST_NAME") {
        setError(`Last name must be at most ${LIMITS.lastNameMax} characters.`);
      } else if (err instanceof ApiError && err.code === "INVALID_AVATAR_COLOR") {
        setError("Pick a color from the palette.");
      } else {
        setError(err instanceof ApiError ? err.message : "Failed to save");
      }
    } finally {
      setSaving(false);
    }
  }

  async function onPickAvatar(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Choose an image file (JPEG, PNG or WebP).");
      setSuccess(null);
      return;
    }

    setError(null);
    setSuccess(null);
    setUploading(true);
    try {
      const jpeg = await resizeImageToJpeg(file);
      const { upload } = await getAvatarUploadUrl();
      if (jpeg.size > LIMITS.avatarMaxBytes) throw new Error("too large");
      await uploadAvatarJpeg(upload, jpeg);
      const me = await fetchMe();
      syncFromProfile(me);
      setSuccess("Avatar updated.");
      window.dispatchEvent(new CustomEvent("profile-updated"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Avatar upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function onRemoveAvatar() {
    const ok = await confirm({
      title: "Remove your photo?",
      message: "Your profile will show your initials instead.",
      confirmLabel: "Remove photo",
      destructive: true,
    });
    if (!ok) return;

    setError(null);
    setSuccess(null);
    setRemoving(true);
    try {
      const me = await deleteAvatar();
      syncFromProfile(me);
      setSuccess("Photo removed.");
      window.dispatchEvent(new CustomEvent("profile-updated"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove photo");
    } finally {
      setRemoving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center text-sm text-muted">Loading…</div>
    );
  }

  return (
    <div className="mx-auto flex min-h-full w-full max-w-lg flex-col px-4 py-10">
      <PageHeader title="Profile" subtitle={user?.email} />

      <div className="rounded-2xl border border-line bg-surface p-6 shadow-sm sm:p-8">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          {profile ? (
            <Avatar
              key={`${profile.avatarKey ?? ""}-${profile.updatedAt ?? ""}`}
              sub={profile.sub}
              screenName={screenName}
              firstName={firstName}
              lastName={lastName}
              color={avatarColor ?? undefined}
              avatarKey={profile.avatarKey}
              googlePicture={profile.googlePicture}
              updatedAt={profile.updatedAt}
              size={96}
            />
          ) : null}
          <div className="flex w-full flex-col items-center gap-2 sm:items-start">
            <p className="text-sm text-muted">
              Upload a square-ish photo — we resize it to 256px JPEG.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickAvatar}
            />
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button
                type="button"
                variant="secondary"
                fullWidth
                className="sm:w-auto"
                disabled={uploading || removing}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? "Uploading…" : "Change avatar"}
              </Button>
              {profile?.avatarKey ? (
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  className="sm:w-auto"
                  disabled={uploading || removing}
                  onClick={() => void onRemoveAvatar()}
                >
                  {removing ? "Removing…" : "Remove photo"}
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <form onSubmit={onSaveProfile} className="mt-8 space-y-4">
          <ErrorBanner message={error} />
          <SuccessBanner message={success} />
          <div>
            <label htmlFor="screenName" className={labelCls}>
              Screen name
            </label>
            <input
              id="screenName"
              className={fieldCls}
              value={screenName}
              maxLength={LIMITS.screenNameMax}
              onChange={(e) => setScreenName(e.target.value)}
              autoComplete="nickname"
              required
            />
            <p className="mt-1 text-xs text-muted-sm">
              {screenName.trim().length}/{LIMITS.screenNameMax}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className={labelCls}>
                First name <span className="font-normal text-muted-sm">(optional)</span>
              </label>
              <input
                id="firstName"
                className={fieldCls}
                value={firstName}
                maxLength={LIMITS.firstNameMax}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div>
              <label htmlFor="lastName" className={labelCls}>
                Last name <span className="font-normal text-muted-sm">(optional)</span>
              </label>
              <input
                id="lastName"
                className={fieldCls}
                value={lastName}
                maxLength={LIMITS.lastNameMax}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>
          <p className="-mt-1 text-xs text-muted-sm">
            When both are filled in, your initials show as first + last initial. Otherwise
            we use the first two letters of your screen name.
          </p>

          <div>
            <span className={labelCls}>Initials color</span>
            <p className="mb-2 text-xs text-muted-sm">
              Used for your circle at the top of the page and next to items you check off.
              Only applies when you have no photo.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAvatarColor(null)}
                aria-pressed={avatarColor === null}
                title="Automatic (based on your screen name)"
                className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold text-muted ${
                  avatarColor === null
                    ? "border-brand-500 ring-2 ring-brand-500/30"
                    : "border-line"
                }`}
              >
                Aa
              </button>
              {AVATAR_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setAvatarColor(color)}
                  aria-pressed={avatarColor === color}
                  aria-label={`Use ${color}`}
                  title={color}
                  className={`h-8 w-8 rounded-full ${
                    avatarColor === color
                      ? "ring-2 ring-offset-2 ring-brand-500"
                      : ""
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <Button type="submit" fullWidth disabled={saving}>
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </form>
      </div>
    </div>
  );
}
